import type { ReactNode } from 'react';
import { Badge, Card, CardBody, CardTitle, CardDescription, Skeleton } from '@vnetwork/ui/primitives';
import { useCurrentEmployee } from '@/api/account';
import { AccountAvatar } from './AccountAvatar';

/**
 * The signed-in employee's identity header (avatar + name + position • department
 * + code/contract badges) in a flat Card. Shared so it renders IDENTICALLY on
 * the account landing and the detailed-info page. `children` render inside the
 * same card below the identity row (e.g. a facts InfoGrid on the landing).
 */
export function AccountIdentityHeader({ children }: { children?: ReactNode }) {
  const { data: emp, isLoading } = useCurrentEmployee();

  if (isLoading || !emp) {
    return <Card flat><CardBody><Skeleton style={{ height: 72 }} /></CardBody></Card>;
  }

  const subtitle = [emp.position_name, emp.department_name].filter(Boolean).join(' • ');

  return (
    <Card flat>
      <CardBody style={{ display: 'grid', gap: children ? 'var(--vn-space-4)' : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)' }}>
          <AccountAvatar name={emp.employee_name} size={56} />
          <div style={{ display: 'grid', gap: 'var(--vn-space-1)', minWidth: 0 }}>
            <CardTitle>{emp.employee_name}</CardTitle>
            {subtitle ? <CardDescription>{subtitle}</CardDescription> : null}
            <div style={{ display: 'inline-flex', gap: 'var(--vn-space-2)', marginTop: 'var(--vn-space-1)' }}>
              {emp.employee_code ? <Badge tone="neutral">{emp.employee_code}</Badge> : null}
              {emp.employee_contract_type ? <Badge tone="primary">{emp.employee_contract_type}</Badge> : null}
            </div>
          </div>
        </div>
        {children}
      </CardBody>
    </Card>
  );
}
